import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Grow } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useSetAtom } from 'jotai';
import CustomDialog from '../CustomDialog';
import EntityAddressesPage from './Pages/EntityAddressesPage';
import EntityGeneralitiesPage from './Pages/EntityGeneralitiesPage';
import EntityOccupationPage from './Pages/EntityOccupationPage';
import CustomNewPracticeActions from '../../../Pages/PracticesPage/Components/CustomNewPracticeActions';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import useAPI from '../../../api';
import { formatPayloadDate } from '../../../Helpers/dates';

export const DEFAULT_PAGES = [
  EntityGeneralitiesPage,
  EntityAddressesPage,
  EntityOccupationPage,
];

const EntityInfoDialog = function EntityInfoDialog({
  title = '',
  open = false,
  close = () => {},
  onSubmit = () => {},
  edit = false,
  pages = DEFAULT_PAGES,
  data = {},
  contact = false,
  isLongChannel = false,
}) {
  const methods = useForm({
    mode: 'onChange',
  });

  const intl = useIntl();
  const API = useAPI();
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const [currentPage, setCurrentPage] = useState(0);
  const [contactTypes, setContactTypes] = useState([]);
  const [leadOrigins, setLeadOrigins] = useState([]);

  const Page = pages[currentPage];

  const handleChangePage = async (nSteps) => {
    const newStepIndex = currentPage + nSteps;
    let result = true;
    if (newStepIndex > currentPage) result = await methods.trigger();
    if (result) setCurrentPage(currentPage + nSteps);
  };

  useEffect(() => {
    if (open && (!edit || contact))
      API.getContactTypes(
        { silent: true },
        (res) => {
          setContactTypes(res);
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.CONTACT_TYPES_LOADING_ERROR,
            severity: 'error',
          });
        }
      );
  }, [open]);

  useEffect(() => {
    if (open && (!edit || contact))
      API.getLeadOrigins(
        { silent: true },
        (res) => {
          setLeadOrigins(
            res.map((item) => ({
              label: item.leadOrigin,
              value: item.id,
            }))
          );
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.LEAD_ORIGINS_LOADING_ERROR,
            severity: 'error',
          });
        }
      );
  }, [open]);

  useEffect(() => {
    if (data !== null) {
      methods.reset({
        ...data,
      });
    }
  }, [data]);

  useEffect(() => {
    if (!open) {
      methods.reset();
      setCurrentPage(0);
    }
  }, [open]);

  const normalizeBirthDate = (birthDate) => {
    if (!birthDate) return null;

    if (birthDate instanceof Date && !isNaN(birthDate.getTime())) {
      return formatPayloadDate(birthDate)?.toISOString();
    }

    return birthDate;
  };

  const handleSubmit = (submitData) => {
    const formattedData = {
      ...submitData,
      birthDate: normalizeBirthDate(submitData?.birthDate),
    };
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <CustomDialog
        title={title}
        open={open}
        close={(e, reason) => {
          if (reason !== 'backdropClick') {
            close(e, methods.formState.isDirty);
          }
        }}
        maxWidth="md"
        fullWidth
        transition={Grow}
        noForm={false}
        onSubmit={handleSubmit}
        components={{
          actions: (
            <CustomNewPracticeActions
              currentPage={currentPage}
              handleChangePage={handleChangePage}
              max={pages.length}
              edit={edit}
              onCancel={(e) => close(e, methods.formState.isDirty)}
            />
          ),
        }}
      >
        <Page
          isLongChannel={isLongChannel}
          errors={methods.formState?.errors}
          data={data}
          formData={methods?.getValues()}
          edit={edit}
          formDirty={methods.formState.isDirty}
          contactTypesOptions={contactTypes}
          leadOriginsOptions={leadOrigins}
          contact={contact}
        />
      </CustomDialog>
    </FormProvider>
  );
};

export default EntityInfoDialog;
